'use client';

import { Consumer, CreateConsumerV1201ResponseData } from '@toki-power/public-api';
import logger from 'logger';
import { useRouter } from 'next/navigation';
import { Suspense, useState } from 'react';

import { useAuthUser } from '~/auth';
import { CreateContractState, CreateContractWizard } from '~/contract';
import { CreateCustomerWizard } from '~/customer';
import { CreateCustomerState } from '~/customer/hooks/useCreateCustomer/types';
import { ResponseError } from '~/data/api';
import { useConsumerApi } from '~/data/api/hooks/useConsumerApi';
import { CreateMeteringPointWizard } from '~/meteringPoint';
import { CreateMeteringPointState } from '~/meteringPoint/hooks/useCreateMeteringPoint/types';
import { CreateAccountState, useOnboardingData } from '~/onboarding';
import {
  createRequest,
  isPartiallyCompletedOnboardingRequest,
  OnboardingRequest,
  updateRequest,
} from '~/services/firestore';

import { prepareOnboardingData } from '../helpers/prepareOnboardingData/prepareOnboardingData';
import { CreateAccountWizard } from './CreateAccountWizard';
import { WizardLoader } from './WizardLoader';

interface onCompletion {
  setWizard: (wizard: string) => void;
  push: (url: string) => void;
  data: OnboardingRequest;
  createConsumer: (data: Consumer) => Promise<CreateConsumerV1201ResponseData>;
}

type onUpdate =
  | CreateAccountState['accountData']
  | CreateCustomerState['customerData']
  | CreateMeteringPointState['meteringPointsData']
  | CreateContractState['contractData'];

const WIZARDS = {
  account: {
    props: {
      onCompletion: ({ setWizard }: onCompletion) => {
        setWizard('customer');
      },
      onUpdate: (data: onUpdate) => {
        createRequest({ accountData: data as CreateAccountState['accountData'] });
      },
    },
    wizard: CreateAccountWizard,
  },
  contract: {
    props: {
      onCompletion: ({ push }: onCompletion) => {
        push('/contracts');
      },
      onUpdate: (data: onUpdate) => {
        updateRequest({ contract: data });
      },
    },
    wizard: CreateContractWizard,
  },
  customer: {
    props: {
      onCompletion: ({ setWizard }: onCompletion) => {
        setWizard('meteringPoint');
      },
      onInvoiceUpload: (data: OnboardingRequest) => {
        updateRequest(data);
      },
      onUpdate: (data: onUpdate) => {
        updateRequest({ customerData: data });
      },
    },
    wizard: CreateCustomerWizard,
  },
  meteringPoint: {
    props: {
      onCompletion: async ({ setWizard, data, createConsumer }: onCompletion) => {
        try {
          if (!isPartiallyCompletedOnboardingRequest(data)) {
            logger.error('Error preparing data - request not completed');
            throw new Error('Request not completed');
          }

          const response = await createConsumer(prepareOnboardingData({ data }));
          await updateRequest({ completed: true, contract: response });

          setWizard('contract');
        } catch (e) {
          logger.error('Error while creating consumer', { e });

          if (e instanceof ResponseError) {
            throw e;
          }
        }
      },
      onUpdate: (data: onUpdate) => {
        updateRequest({ meteringPointsData: data });
      },
      skipSteps: ['accountOptions'],
    },
    wizard: CreateMeteringPointWizard,
  },
};

export const SalesOnboardingWizard = () => {
  const { push } = useRouter();
  const { isLoading: isLoadingUser } = useAuthUser();

  const [currentWizard, setCurrentWizard] = useState('account');
  const { data, isLoading: isLoadingData, refetch } = useOnboardingData({ currentWizard });
  const { createConsumer } = useConsumerApi();

  const CurrentWizard = WIZARDS[currentWizard as keyof typeof WIZARDS].wizard;

  const onCompletionCallback = WIZARDS[currentWizard as keyof typeof WIZARDS].props.onCompletion;

  const { props } = WIZARDS[currentWizard as keyof typeof WIZARDS];
  const wizardProps = {
    ...props,
    onboardingData: data,
  };

  const onCompletion = async () => {
    const { data: refetchedData = {} } = await refetch();

    await onCompletionCallback({ createConsumer, data: refetchedData || {}, push, setWizard: setCurrentWizard });
  };

  if (isLoadingUser || isLoadingData) return <WizardLoader currentWizard={currentWizard} />;

  return (
    <Suspense fallback={<WizardLoader currentWizard={currentWizard} />}>
      <CurrentWizard {...wizardProps} onCompletion={onCompletion} />
    </Suspense>
  );
};
